
import StringUtils from "@/utils/StringUtils.js";
import RcDomainObject from "../../common/RcDomainObject";
import Address from "../address/Address.js";
import AddressMap from "../address/AddressMap.js";
import FeatureMap from "../feature/FeatureMap.js";
import CompanyMap from "./CompanyMap.js";
import User from "@/domain/model/user/User.js";
import RcTime from "../../session/time/RcTime.js";

export default class Company extends RcDomainObject {

  static MODEL_NAME = 'Company';

  static FIELDS = {
    NAME: 'name',
    WEBSITE: 'website',

    EMAIL: 'email',
    EMAIL_DEFECTS: 'emailDefects',
    EMAIL_EQUIPMENT: 'emailEquipment',
    EMAIL_EQUIPMENT_DEFECTS: 'emailEquipmentDefects',

    EMAIL_LOW_BATTERY_LIST: 'lowBattery',
    EMAIL_OUTSIDE_160_LIST: 'outside160',
    EMAIL_LEAVE_RETURN_LIST: 'leaveReturn',
    EMAIL_UNEXPECTED_MOVEMENT_LIST: 'unexpectedMovement',
    EMAIL_SPEEDING_LIST: 'speeding',

    CREDIT: 'credit',
    CVOR_NUMBER: 'cvor',
    PHONE: 'phone',
    POSTAL_CODE: 'postal',
    ADDRESS: "Addressv2",
    FEATURES: FeatureMap.MODEL_NAME,
    COUNTRY: 'country',
    PROVINCE: 'jurisdiction',
    DISCOUNT: 'discount',
    BAY: 'bay',
    PROMO: 'promoCode',
    CONTRACT_START: 'contractStart',
    BILLING_PERIOD: 'billingPeriod',
    STATE: 'state',
    INTEGRATION_REFERENCE: 'integrationReference',
    RENEWAL_DATE: 'cmvRegDate',
    API_KEY: 'apiKey',
    ADMIN_ORDER: 'order',
    ADMIN_SUMMARY: 'adminSummary',
    ADMIN_NOTES: 'adminNotes',
    ADDRESS_HQ: 'hq',
    ADDRESS_OTHERS: 'AddressMap',
    ASSETS: 'AssetMap',
    AUTO_CHARGE_INVOICE: "chargeInvoice",
    AUTO_EMAIL_INVOICE: "emailInvoice",
    AUTO_DECLINE_INVOICE: "declineInvoice" ,
    AUTO_SEND_WELCOME: "welcomeEmail" ,
    
    EMAIL_INSPECTIONS_TO_DRIVERS: "emailInspectionsToDrivers",
    TIMESHEET_STARTED_BEFORE_INSPECTION: "timesheetStartedBeforeInspection",
    PARENT: "parent",
    DIVISION_NAME: 'divisionName',
    ONBOARDING: "onboarding",
  }

  static Values = {
    States: {
      ACTIVE: 'active',
      INACTIVE: 'inactive',
    },
    BillingPeriod: {
      MONTHLY: 'monthly',
    },
    Discount: {
      FULL: 1,
      HALF: 0.5,
      THIRD: 0.3,
      QUARTER: 0.25,
      FIFTH: 0.2,
      TENTH: 0.1,
    }
  }

  constructor(domain, data) {
    super(domain, data, Company.MODEL_NAME);
  }

  fields() {
    return Company.FIELDS;
  }

  find() {
    let cmpy = this.domain.companys().findById(this.id());
    if (cmpy.isNew()) {
      cmpy.withId(this.id());
    }
    return cmpy;
  }

  update() {
    var update = this.find();
    this.data = update.data;
    return this;
  }

  order() {
    return this.getString(Company.FIELDS.ADMIN_ORDER);
  }

  apiKey() {
    return this.getString(Company.FIELDS.API_KEY);
  }

  name() {
    let _name = this.getString(Company.FIELDS.NAME);
    return _name;
  }

  autoEmailInvoice() {
    return this.getBoolean(Company.FIELDS.AUTO_EMAIL_INVOICE);
  }

  autoChargeInvoice() {
    return this.getBoolean(Company.FIELDS.AUTO_CHARGE_INVOICE);
  }

  autoDeclineInvoice() {
    return this.getBoolean(Company.FIELDS.AUTO_DECLINE_INVOICE);
  }

  withAutoDeclineInvoice(value) {
    this.put(Company.FIELDS.AUTO_DECLINCE_INVOICE, value);
    return this;
  }

  autoSendWelcome() {
    return this.getBoolean(Company.FIELDS.AUTO_SEND_WELCOME);
  }

  withAutoSendWelcome(value) {
    this.put(Company.FIELDS.AUTO_SEND_WELCOME, value);
    return this;
  }

  withName(name) {
    this.put(Company.FIELDS.NAME, name);
    return this;
  }

  registrationDateV2() {
    let rcDate = RcTime.time().at(this.getInt(Company.FIELDS.RENEWAL_DATE), this.timeZone());
    return rcDate;
  }

  website() {
    return this.getString(Company.FIELDS.WEBSITE);
  }

  email() {
    return this.getString(Company.FIELDS.EMAIL);
  }

  emailDefects() {
    return this.getString(Company.FIELDS.EMAIL_DEFECTS);
  }

  emailEquipment() {
    return this.getString(Company.FIELDS.EMAIL_EQUIPMENT);
  }

  emailEquipmentDefects() {
    return this.getString(Company.FIELDS.EMAIL_EQUIPMENT_DEFECTS);
  }

  lowBatteryEmailList() {
    return this.getString(Company.FIELDS.EMAIL_LOW_BATTERY_LIST);
  }

  outside160EmailList() {
    return this.getString(Company.FIELDS.EMAIL_OUTSIDE_160_LIST);
  }

  leaveReturnEmailList() {
    return this.getString(Company.FIELDS.EMAIL_LEAVE_RETURN_LIST);
  }

  unexpectedMovementEmailList() {
    return this.getString(Company.FIELDS.EMAIL_UNEXPECTED_MOVEMENT_LIST);
  }

  cvorNumber() {
    return this.getString(Company.FIELDS.CVOR_NUMBER);
  }

  phone() {
    return this.getString(Company.FIELDS.PHONE);
  }

  postal() {
    return this.getString(Company.FIELDS.POSTAL_CODE);
  }

  country() {
    return this.getString(Company.FIELDS.COUNTRY);
  }

  credit() {
    return this.getString(Company.FIELDS.CREDIT);
  }

  adminSummary() {
    return this.getString(Company.FIELDS.ADMIN_SUMMARY);
  }

  adminNotes() {
    return this.getString(Company.FIELDS.ADMIN_NOTES);
  }

  creditDisplay() {
    var credit = this.credit();
    if (StringUtils.isEmpty(credit)) {
      credit = "0";
    }
    credit = parseFloat(credit);
    return StringUtils.format("${0}", [credit.toFixed(2)]);
  }

  stats() {
    return this.domain.companys().findStatsById(this.id());
  }

  address() {
    var obj = this.get(Company.FIELDS.ADDRESS);
    var address = new Address(this.domain, obj);
    return address;
  }

  withAddress(address) {
    this.put(Company.FIELDS.ADDRESS, address);
    return this;
  }

  addressHq() {
    var obj = this.get(Company.FIELDS.ADDRESS_HQ);
    var address = new Address(this.domain, obj);
    return address;
  }

  withAddressHq(address) {
    this.put(Company.FIELDS.ADDRESS_HQ, address);
    return this;
  }

  
  addressOthers() {
    var obj = this.get(Company.FIELDS.ADDRESS_OTHERS);
    var address = new AddressMap(this.domain, obj);
    return address;
  }

  withAddressOthers(addressMap) {
    this.put(Company.FIELDS.ADDRESS_OTHERS, addressMap);
    return this;
  }

  invoiceSettings() {
    var settings = this.domain.invoices().settings().findByCompany(this);
    return settings;
  }

  assets() {
    var assets = this.domain.assets().findByCompany(this);
    return assets;
  }

  features() {
    let obj = this.get(Company.FIELDS.FEATURES);
    var featureMap = new FeatureMap(this.domain, obj);
    if (obj == null) {
      this.withFeatures(featureMap);
    }
    return featureMap;
  }

  withFeatures(features) {
    this.with(features).done();
    return this;
  }

  owners() {
    return this.employees().owners();
  }

  employees() {
    return this.domain.employees().findByCompany(this.id());
  }

  copy() {
    var data = this._copy();
    return new Company(this.domain, data);
  }

  hasCheckIns() {
    return this.features().find().allowsCheckIns();
  }

  checklists() {
    return this.domain.checklists().findByCompany(this);
  }

  hasChecklists() {
    return this.features().find().allowsChecklists();
  }

  hasChecklistsForVehicles() {
    return this.features().find().allowsChecklistsForVehicles();
  }

  hasInspections() {
    return this.features().find().allowsInspections();
  }

  hasTrackLocation() {
    return this.features().find().allowsTrackLocation();
  }

  hasTrackSleeper() {
    return this.features().find().hasTrackSleeper();
  }

  hasTimesheets() {
    return this.features().find().allowsTimesheets();
  }

  hasMap() {
    return this.features().find().allowsMap();
  }

  hasVehicles() {
    return this.features().find().allowsVehicles();
  }

  vehicles() {
    return this.domain.vehicles().findByCompany(this);
  }

  hasSso() {
    return this.features().find().allowsSso();
  }

  hasOffline() {
    return this.features().find().allowsOffline();
  }

  hasCompliance() {
    return this.features().find().allowsCompliance();
  }

  hasEquipment() {
    return this.features().find().allowsEquipment();
  }

  hasEquipmentInspections() {
    return this.features().find().allowsEquipmentInspections();
  }

  hasGpsDevice() {
    return this.features().find().hasGpsDevice();
  }

  gpsDevices() {
    return this.domain.gpsDevices().findByCompany(this);
  }
  
  hasFlatRate() {
    return this.features().find().hasFlatRate();
  }

  hasSupport() {
    return this.features().find().hasSupport();
  }

  hasHome() {
    return this.features().find().allowsHome();
  }

  hasMessages() {
    return this.features().find().allowsMessages();
  }

  hasUsers() {
    return this.features().find().allowsUsers();
  }

  emailInvoices() {
    return this.autoEmailInvoice();
  }

  chargeInvoices() {
    return this.autoChargeInvoice();
  }

  discount() {
    return this.getString(Company.FIELDS.DISCOUNT);
  }

  promo() {
    return this.getString(Company.FIELDS.PROMO);
  }

  hasPromo() {
    return StringUtils.isNotEmpty(this.promo());
  }

  contractStart() {
    return this.getString(Company.FIELDS.CONTRACT_START);
  }

  contractStartDate() {
    var date = this.domain.session().time().at(this.get(Company.FIELDS.CONTRACT_START));
    return date;
  }

  billingPeriod() {
    return this.getString(Company.FIELDS.BILLING_PERIOD);
  }

  billingPeriodDisplay() {
    if (this.billingPeriod() == "monthly") {
      return "Monthly";
    }
    if (this.billingPeriod() == "quarterly") {
      return "Quarterly";
    }
    if (this.billingPeriod() == "semi-annually") {
      return "Semi Annually";
    }
    if (this.billingPeriod() == "annually") {
      return "Annually";
    }
    return "Monthly";
  }

  bay() {
    return this.getString(Company.FIELDS.BAY);
  }


  hq() {
    return this.getString(Company.FIELDS.HQ);
  }

  withStateActive() {
    this.put(Company.FIELDS.STATE, Company.Values.States.ACTIVE);
    return this;
  }

  withBillingPeriodMonthly() {
    this.put(Company.FIELDS.BILLING_PERIOD, Company.Values.BillingPeriod.MONTHLY);
    return this;
  }

  withFullDiscount() {
    this.put(Company.FIELDS.DISCOUNT, Company.Values.Discount.FULL);
    return this;
  }

  hasDiscount() {
    return StringUtils.eic(Company.Values.Discount.FULL, this.discount());
  }

  userCanUpdateProfile() {
    return this.features().find().allowsProfileUpdate();
  }

  toggleUserCanUpdateProfile() {
    let feature = this.domain.features().profileUpdate()
    let existing = this.features().find().allowsProfileUpdate();
    let features = this.features();
    if (existing) {
      features.remove(feature);
    } else {
      features.add(feature);
    }
    this.withFeatures(features);
  }

  withUserCanUpdateProfile(value) {
    let features = this.features();
    let feature = this.domain.features().profileUpdate()
    if (value) {
      features.add(feature);
    } else {
      features.remove(feature);
    }
    this.withFeatures(features);
    return this;
  }
  
  emailInspectionsToDriver() {
    if (this.isNotNull(Company.FIELDS.EMAIL_INSPECTIONS_TO_DRIVERS)) {
      return this.getBoolean(Company.FIELDS.EMAIL_INSPECTIONS_TO_DRIVERS);
    }
    return true;
  }

  withEmailInspectionsToDriver(value) {
    this.put(Company.FIELDS.EMAIL_INSPECTIONS_TO_DRIVERS, value);
    return this;
  }

  toggleEmailInspectionsToDriver() {
    return this.withEmailInspectionsToDriver(!this.emailInspectionsToDriver());
  }

  timesheetStartedBeforeInspection() {
    if (this.isNotNull(Company.FIELDS.TIMESHEET_STARTED_BEFORE_INSPECTION)) {
      return this.getBoolean(Company.FIELDS.TIMESHEET_STARTED_BEFORE_INSPECTION);
    }
    return true;
  }

  withTimesheetStartedBeforeInspection(value) {
    this.put(Company.FIELDS.TIMESHEET_STARTED_BEFORE_INSPECTION, value);
    return this;
  }

  toggleTimesheetStartedBeforeInspection() {
    return this.withTimesheetStartedBeforeInspection(!this.timesheetStartedBeforeInspection());
  }

  integrationReference() {
    return this.get(Company.FIELDS.INTEGRATION_REFERENCE);
  }

  pointer() {
    var company = new Company(this.domain);
    company.withId(this.id());
    return company;
  }

  map() {
    var map = new CompanyMap(this.domain);
    map.add(this);
    return map;
  }

  employeeForUser(user) {
    return this.domain.employees().findForCompanyAndUser(this, user);
  }

  modifiedBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.MODIFIED_BY));
  }

  createdBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.CREATED_BY));
  }

  isPaymentMethodCreditCard() {
    return this.invoiceSettings().isPaymentMethodCreditCard();
  }

  hasCard() {
    return this.invoiceSettings().hasCard();
  }

  withParent(parent) {
    this.put(Company.FIELDS.PARENT, parent.id());
    return this;
  }

  parent() {
    return new Company(this.domain, this.get(Company.FIELDS.PARENT));
  }

  hasFeatureDivisions() {
    return this.features().find().allowsDivisions();
  }

  isDivision() {
    return this.parent().isNotNew();
  }

  divisions() {
    if (this.isDivision()) {
      const employee = this.domain.session().employee();
      if (employee.company().id() === this.parent().id()) {
        const map = this.domain.companys().findDivisionsForCompany(this.parent());
        map.add(this.parent().find());
        return map;
      }
    }
    const map = this.domain.companys().findDivisionsForCompany(this);
    return map;
  }

  withDivisionName(name) {
    this.put(Company.FIELDS.DIVISION_NAME, name);
    return this;
  }

  divisionName() {
    let name = this.getString(Company.FIELDS.DIVISION_NAME);
    if (StringUtils.isEmpty(name)) {
      name = this.name();
    }
    return name;
  }
  
  hasFeatureLabels() {
    return this.features().find().allowsLabels();
  }

  labels() {
    const map = this.domain.labels().findByCompany(this);
    return map;
  }

  onboardingEmails() {
    return this.getString(Company.FIELDS.ONBOARDING);
  }

  onboardingEmailDateFor(emailType) {
    let value = this.onboardingEmails();
    if (StringUtils.isEmpty(value)) {
      return "";
    }
    let parts = value.split("|");
    for (let part of parts) {
      let pair = part.split(":");
      if (pair.length >= 2 && StringUtils.eic(pair[0], emailType)) {
        return pair[1];
      }
    }
    return "";
  }
}